import { formatMinutes } from "@/components/entry/RuntimeChip.tsx";
import { formatClock, formatDeadline, formatHoursPerDay } from "@/lib/countdown/format.ts";
import type { LegId, LegPace } from "@/lib/countdown/legs.ts";
import styles from "./countdown.module.css";
import { InfeasibleCallout } from "./InfeasibleCallout.tsx";
import { PaceMeter } from "./PaceMeter.tsx";
import { ProgressRing } from "./ProgressRing.tsx";

/** What each scope is called. The bar reads the same whether there are one or two. */
export const LEG_LABEL: Record<LegId, string> = {
  road: "The whole road",
  "leg-1": "Leg 1",
  "leg-2": "Leg 2",
};

const LEG_SCOPE: Record<LegId, string> = {
  road: "every stop",
  "leg-1": "through Endgame",
  "leg-2": "after Endgame",
};

/**
 * One deadline, and everything you'd want to know about whether you'll make it:
 * days left (ticking), what's still unwatched, and the hours per day that
 * implies. The pace meter turns the last number into a picture, and an
 * infeasible leg carries its own way out.
 */
export function CountdownLeg({ leg }: { leg: LegPace }) {
  const label = LEG_LABEL[leg.id];

  return (
    <article className={styles.leg} data-status={leg.status} data-leg={leg.id}>
      <div className={styles.legHead}>
        <ProgressRing progress={leg.progress} status={leg.status} label={label} />
        <div className={styles.legTitles}>
          <p className={styles.legLabel}>
            {label} <span className={styles.legScope}>{LEG_SCOPE[leg.id]}</span>
          </p>
          <p className={styles.days}>
            <strong className={styles.daysNumber}>{leg.daysLeft}</strong>
            <span className={styles.daysUnit}>{leg.daysLeft === 1 ? "day" : "days"}</span>
            <span className={styles.clock}>{formatClock(leg.msLeft)}</span>
          </p>
          <p className={styles.deadline}>
            until <strong>{leg.eventTitle}</strong> · {formatDeadline(leg.deadline)}
          </p>
        </div>
      </div>

      <dl className={styles.stats}>
        <div className={styles.stat}>
          <dt>Left to watch</dt>
          <dd>
            {leg.complete ? (
              "nothing, this leg is done"
            ) : (
              <>
                {formatMinutes(leg.remainingMinutes)}{" "}
                <span className={styles.statMuted}>
                  · {leg.remainingCount} of {leg.entryCount} stops
                </span>
              </>
            )}
          </dd>
        </div>
        <div className={styles.stat}>
          <dt>Required</dt>
          <dd>
            {formatHoursPerDay(leg.requiredHoursPerDay)}{" "}
            <span className={styles.statMuted}>h/day</span>
          </dd>
        </div>
      </dl>

      <PaceMeter leg={leg} />

      {leg.status === "infeasible" && <InfeasibleCallout leg={leg} legLabel={label} />}
    </article>
  );
}
